import { useEffect, useState } from "react";
import ProductCard from "../components/ProductCard";
import { Search } from "lucide-react";
import { fetchProductsSearch, fetchCategories } from "../services/productApi";
import { useTranslation } from "react-i18next";

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // Load categories once
  useEffect(() => {
    fetchCategories().then(setCategories);
  }, []);

  // Load products when filters change
  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
        const params = {
      search: searchTerm,
    };

    if (activeCategory !== "all") {
      params.category = activeCategory;
    }

    const data = await fetchProductsSearch(params);
      setProducts(data);
      setLoading(false);
    };

    loadProducts();
  }, [activeCategory, searchTerm]);

  return (
    <div>
      {/* Header */}
      <section className="bg-primary text-primary-foreground section-padding">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">
           {t("admin.products.title")}
          </h1>
          <p className="text-primary-foreground/80 max-w-2xl mx-auto">
           {t("admin.products.subtitle")}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-secondary sticky top-20 z-40">
        <div className="container-custom">
        <div className="space-y-6">
            {/* Search */}
            <div className="relative w-full max-w-xl mx-auto">
              <Search
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                placeholder={t("products.search")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-full bg-card border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none"
              />
            </div>

            {/* Categories */}
            <div className="flex gap-2 overflow-x-auto whitespace-nowrap no-scrollbar max-w-full">
              {/* All button */}
              <button
                onClick={() => setActiveCategory("all")}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === "all"
                    ? "bg-primary text-primary-foreground shadow-lg scale-105"
                    : "bg-card text-foreground/70 hover:bg-accent hover:text-accent-foreground hover:shadow"
                }`}
              >
               {t("products.all")}
              </button>

              {/* Category buttons */}
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-6 py-2.5 rounded-full text-base font-extrabold transition-all duration-300 ${
                    activeCategory === category.id
                      ? "bg-primary text-primary-foreground shadow-lg scale-105"
                      : "bg-card text-foreground/70 hover:bg-accent hover:text-accent-foreground hover:shadow"
                  }`}
                >
                 {lang === "ar" ? category.name_ar : category.name_en}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <p className="text-center">{t("products.loading")}</p>
          ) : products.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {products.map((product, index) => (
                <div
                  key={product.id}
                  className="animate-slide-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg">
                {t("admin.products.noProducts")}
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Products;
